import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCampaignFilter } from '@/hooks/useCampaignFilter';
import { useDeliverableAggregates } from '@/hooks/useDeliverableAggregates';
import { useCampaignBudgetCalculations } from '@/hooks/useCampaignBudgetCalculations';
import { useCampaignPerformanceTimeSeries } from '@/hooks/useCampaignPerformanceTimeSeries';
import { usePlatformDistribution } from '@/hooks/usePlatformDistribution';
import { useTopPerformingPosts } from '@/hooks/useTopPerformingPosts';
import { useAllInfluencers } from '@/hooks/useAllInfluencers';
import { getCurrencySymbol } from '@/utils/currency';
import { useAuth } from '@/contexts/AuthContext';
import { AnalyticsHeader } from '../header/AnalyticsHeader';
import { KPICardsGrid } from '../metrics/KPICardsGrid';
import { QuickStatsSection } from '../metrics/QuickStatsSection';
import { AnalyticsChartsGrid } from '../charts/AnalyticsChartsGrid';
import { TopPerformersSection } from '../tables/TopPerformersSection';
import { useAnalyticsCalculations } from '../../hooks/useAnalyticsCalculations';

export const AnalyticsView: React.FC = () => {
  const { userCountry } = useAuth();
  const symbol = getCurrencySymbol(userCountry);
  const [searchParams, setSearchParams] = useSearchParams();
  const campaignParam = searchParams.get('campaignId') || searchParams.get('campaign') || '';
  const [selectedCampaign, setSelectedCampaign] = useState<string>(campaignParam);
  const [showAllInfluencers, setShowAllInfluencers] = useState<boolean>(false);
  const [showAllPosts, setShowAllPosts] = useState<boolean>(false);

  const { data: campaigns = [], isLoading: campaignsLoading } = useCampaignFilter();

  // Sync selected campaign when campaigns are loaded or when query param changes
  useEffect(() => {
    if (campaignParam && campaigns.some((c) => c.id === campaignParam)) {
      setSelectedCampaign(campaignParam);
    } else if (campaigns.length > 0 && !selectedCampaign) {
      setSelectedCampaign(campaigns[0].id);
    }
  }, [campaigns, campaignParam, selectedCampaign]);

  // Data fetching hooks for selected campaign
  const { data: deliverableAggregates = [], isLoading: aggregatesLoading } =
    useDeliverableAggregates(selectedCampaign);

  const {
    data: campaignPerformance = [],
    isLoading: performanceLoading,
    isError: performanceError,
    refetch: refetchPerformance,
  } = useCampaignPerformanceTimeSeries(selectedCampaign);

  const { data: budgetData } = useCampaignBudgetCalculations(selectedCampaign);

  const {
    data: platformDistribution = [],
    isLoading: platformLoading,
    isError: platformError,
    refetch: refetchPlatform,
  } = usePlatformDistribution(selectedCampaign);

  const { data: allPosts = [], isLoading: postsLoading } = useTopPerformingPosts(selectedCampaign);
  const { data: allInfluencers = [] } = useAllInfluencers();

  // Pure calculations hook
  const { totalViews, stats, topInfluencers, topPosts, kpiCards } = useAnalyticsCalculations({
    deliverableAggregates,
    budgetData,
    allPosts,
    allInfluencers,
    showAllInfluencers,
    showAllPosts,
    userCountry,
    symbol,
  });

  const selectedCampaignName = useMemo(() => {
    const campaign = campaigns.find((c) => c.id === selectedCampaign);
    return campaign?.name || '';
  }, [campaigns, selectedCampaign]);

  const handleSelectCampaign = (val: string) => {
    setSelectedCampaign(val);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('campaignId', val);
      return next;
    });
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <AnalyticsHeader
        selectedCampaign={selectedCampaign}
        onSelectCampaign={handleSelectCampaign}
        campaigns={campaigns}
        campaignsLoading={campaignsLoading}
        selectedCampaignName={selectedCampaignName}
      />

      {/* KPI Cards */}
      <KPICardsGrid cards={kpiCards} />

      {/* Quick Stats (Engagement Metrics & Financial Analysis) */}
      <QuickStatsSection stats={stats} />

      {/* Charts Grid */}
      <AnalyticsChartsGrid
        campaignPerformance={campaignPerformance}
        performanceLoading={performanceLoading}
        performanceError={performanceError}
        onRetryPerformance={refetchPerformance}
        platformDistribution={platformDistribution}
        platformLoading={platformLoading}
        platformError={platformError}
        onRetryPlatform={refetchPlatform}
        totalViews={totalViews}
      />

      {/* Top Performers (Influencers & Posts) */}
      <TopPerformersSection
        topInfluencers={topInfluencers}
        aggregatesLoading={aggregatesLoading}
        onToggleShowAllInfluencers={() => setShowAllInfluencers((v) => !v)}
        topPosts={topPosts}
        postsLoading={postsLoading}
        onToggleShowAllPosts={() => setShowAllPosts((v) => !v)}
      />
    </div>
  );
};

export default AnalyticsView;
